<template>
  <Dialog
    v-model:visible="dialogVisible"
    :header="title"
    :modal="true"
    :closable="true"
    :style="{
      width: '90vw',
      maxWidth: '1200px',
      minWidth: '800px'
    }"
    @hide="onHide"
  >
    <div class="video-frame-selector-content">
      <!-- 隐藏的视频元素（用于加载视频数据） -->
      <video
        ref="videoRef"
        :src="videoSrcUrl"
        style="display: none"
        crossorigin="anonymous"
        @loadedmetadata="onVideoLoaded"
        @timeupdate="onTimeUpdate"
        @error="onVideoError"
        @canplay="onVideoCanPlay"
        @seeking="onVideoSeeking"
        @seeked="onVideoSeeked"
      />

      <!-- 控制区域 -->
      <div class="controls-section mb-4">
        <div class="flex items-center gap-4 mb-4">
          <!-- 时间滑块和播放按钮 -->
          <div class="flex-1">
            <div class="flex items-center gap-2 mb-2">
              <label class="text-sm font-medium">
                {{ $t('workflowApps.selectFrame') }}
              </label>
              <Button
                :icon="isPlaying ? 'pi pi-pause' : 'pi pi-play'"
                size="small"
                :label="
                  isPlaying ? $t('workflowApps.pause') : $t('workflowApps.play')
                "
                severity="secondary"
                :disabled="!videoLoaded"
                @click="togglePlay"
              />
              <span v-if="videoLoaded" class="text-xs text-surface-400 ml-2">
                {{ formatTimeWithMs(currentTime) }}
              </span>
              <span v-if="videoLoaded" class="text-xs text-surface-500 ml-2">
                {{ currentFrame }} / {{ totalFrames }}
              </span>
            </div>
            <div class="flex items-center gap-2">
              <span class="text-xs text-surface-500 w-20">
                {{ formatTimeWithMs(currentTime) }}
              </span>
              <input
                :value="currentTime"
                type="range"
                :min="0"
                :max="videoDuration"
                :step="0.1"
                class="flex-1 time-slider"
                @input="onTimeSliderChange"
              />
              <span class="text-xs text-surface-500 w-20">
                {{ formatTimeWithMs(videoDuration) }}
              </span>
            </div>
          </div>
        </div>

        <!-- 预览区域 -->
        <div class="preview-section">
          <label class="text-sm font-medium mb-2 block">
            {{ $t('workflowApps.framePreview') }}
          </label>
          <div class="preview-container">
            <canvas ref="canvasRef" class="preview-canvas"></canvas>
            <div
              v-if="!videoLoaded || isDownloading"
              class="preview-placeholder"
            >
              <ProgressSpinner />
              <p v-if="isDownloading" class="mt-2 text-sm text-surface-500">
                正在下载视频... {{ downloadProgress }}%
              </p>
              <p v-else class="mt-2 text-sm text-surface-500">
                {{ $t('workflowApps.loadingVideo') }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- 操作按钮 -->
      <div class="flex justify-end gap-2">
        <Button
          :label="$t('workflowApps.cancel')"
          severity="secondary"
          @click="onCancel"
        />
        <Button
          :label="$t('workflowApps.confirmSelect')"
          :disabled="!videoLoaded"
          @click="onConfirm"
        />
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import ProgressSpinner from 'primevue/progressspinner'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

import { FASTAPI_CONFIG } from '@/config/fastapi'

interface VideoFrameSelectorProps {
  visible: boolean
  videoUrl: string
  title?: string
}

const props = withDefaults(defineProps<VideoFrameSelectorProps>(), {
  title: '选择视频帧'
})

const emit = defineEmits<{
  'update:visible': [value: boolean]
  confirm: [blob: Blob, time: number]
  cancel: []
}>()

const dialogVisible = computed({
  get: () => props.visible,
  set: (value: boolean) => emit('update:visible', value)
})

const videoRef = ref<HTMLVideoElement | null>(null)
const canvasRef = ref<HTMLCanvasElement | null>(null)
const videoLoaded = ref(false)
const videoDuration = ref(0)
const currentTime = ref(0)
const isPlaying = ref(false)
const videoFrameRate = ref(30) // 默认帧率，会在加载时检测
let animationFrameId: number | null = null
const isSeekingManually = ref(false) // 标记是否正在手动定位

// 视频下载相关状态
const isDownloading = ref(false)
const downloadProgress = ref(0)
const localVideoUrl = ref('')

// 将OBS URL转换为代理URL
const getProxyUrl = (url: string): string => {
  // 如果是OBS链接，使用代理接口
  if (url.includes('obs.cn-south-1.myhuaweicloud.com')) {
    // 提取文件名部分
    const urlParts = url.split('/')
    const filename = urlParts[urlParts.length - 1].split('?')[0] // 去掉可能存在的query参数
    // 使用后端代理URL
    return `${FASTAPI_CONFIG.BASE_URL}/api/v1/upload/proxy/${filename}`
  }
  return url
}

// 计算视频源URL（优先使用本地URL，否则使用代理URL）
const videoSrcUrl = computed(() => {
  if (localVideoUrl.value) {
    return localVideoUrl.value
  }
  return getProxyUrl(props.videoUrl)
})

// 格式化时间显示（含毫秒）
const formatTimeWithMs = (seconds: number): string => {
  if (!isFinite(seconds) || isNaN(seconds)) return '00:00.000'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  const ms = Math.floor((seconds % 1) * 1000)
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`
}

// 计算当前帧数
const currentFrame = computed(() => {
  if (!videoLoaded.value || videoDuration.value === 0) return 0
  return Math.floor(currentTime.value * videoFrameRate.value) + 1
})

// 计算总帧数
const totalFrames = computed(() => {
  if (!videoLoaded.value || videoDuration.value === 0) return 0
  return Math.ceil(videoDuration.value * videoFrameRate.value)
})

// 视频加载完成
const onVideoLoaded = async () => {
  if (videoRef.value) {
    videoDuration.value = videoRef.value.duration
    currentTime.value = 0
    videoLoaded.value = true
    videoFrameRate.value = 30

    // 确保视频定位到第一帧并等待渲染完成
    videoRef.value.currentTime = 0

    // 等待视频seeked事件，确保帧已经准备好
    await new Promise<void>((resolve) => {
      const onSeeked = () => {
        videoRef.value?.removeEventListener('seeked', onSeeked)
        resolve()
      }
      videoRef.value?.addEventListener('seeked', onSeeked)

      // 如果已经在第一帧，直接触发seeked
      if (videoRef.value?.currentTime === 0) {
        setTimeout(() => {
          videoRef.value?.removeEventListener('seeked', onSeeked)
          resolve()
        }, 100)
      }
    })

    // 现在更新预览
    await updatePreview()
  }
}

// 视频时间更新
const onTimeUpdate = () => {
  if (videoRef.value) {
    // 如果正在手动定位，忽略时间更新事件
    if (isSeekingManually.value) {
      return
    }

    currentTime.value = videoRef.value.currentTime
    // 如果视频播放结束，停止播放
    if (videoRef.value.ended) {
      stopPlay()
    }
  }
}

// 视频错误处理
const onVideoError = (event: Event) => {
  console.error('视频加载错误:', event)
}

// 视频可以播放
const onVideoCanPlay = () => {
  // 视频可以播放
}

// 视频正在定位
const onVideoSeeking = () => {
  // 视频正在定位
}

// 视频定位完成
const onVideoSeeked = () => {
  // 视频定位完成
}

// 下载视频到本地
const downloadVideoToLocal = async (videoUrl: string): Promise<string> => {
  isDownloading.value = true
  downloadProgress.value = 0

  try {
    // 使用代理URL（如果是OBS链接）
    const proxyUrl = getProxyUrl(videoUrl)
    const response = await fetch(proxyUrl)

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    const contentLength = response.headers.get('content-length')
    const total = contentLength ? parseInt(contentLength, 10) : 0

    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error('无法获取响应流')
    }

    const chunks: Uint8Array[] = []
    let received = 0

    while (true) {
      const { done, value } = await reader.read()

      if (done) break

      chunks.push(value)
      received += value.length

      if (total > 0) {
        downloadProgress.value = Math.round((received / total) * 100)
      }
    }

    // 合并所有数据块
    const videoData = new Uint8Array(received)
    let position = 0
    for (const chunk of chunks) {
      videoData.set(chunk, position)
      position += chunk.length
    }

    // 创建Blob和本地URL
    const videoBlob = new Blob([videoData], { type: 'video/mp4' })
    const localUrl = URL.createObjectURL(videoBlob)

    return localUrl
  } catch (error) {
    console.error('视频下载失败:', error)
    throw error
  } finally {
    isDownloading.value = false
  }
}

// 时间滑块变化
const onTimeSliderChange = async (event: Event) => {
  if (!videoRef.value || !videoLoaded.value) {
    return
  }

  const target = event.target as HTMLInputElement
  const newTime = parseFloat(target.value)

  // 设置手动定位标记，防止onTimeUpdate干扰
  isSeekingManually.value = true

  // 暂停播放以避免冲突
  const wasPlaying = isPlaying.value
  if (wasPlaying) {
    stopPlay()
  }

  // 更新当前时间
  currentTime.value = newTime

  // 尝试设置视频时间
  videoRef.value.currentTime = newTime

  // 短暂延迟
  await new Promise((resolve) => setTimeout(resolve, 100))

  // 检查设置后的时间
  const actualTime = videoRef.value.currentTime

  if (Math.abs(actualTime - newTime) > 0.2) {
    // 视频无法定位，使用模拟预览
    await updateSimulatedPreview(newTime)
  } else {
    // 视频定位成功，使用实际预览
    await updatePreview()
  }

  // 清除手动定位标记
  isSeekingManually.value = false

  // 如果之前在播放，恢复播放
  if (wasPlaying) {
    playVideo()
  }
}

// 切换播放/暂停
const togglePlay = () => {
  if (isPlaying.value) {
    stopPlay()
  } else {
    playVideo()
  }
}

// 播放视频
const playVideo = () => {
  if (!videoRef.value || !videoLoaded.value) return

  isPlaying.value = true
  void videoRef.value.play()

  // 使用 requestAnimationFrame 更新 canvas
  const updateFrame = () => {
    if (!videoRef.value || !isPlaying.value) return

    void updatePreview()
    currentTime.value = videoRef.value.currentTime

    // 如果视频未结束，继续下一帧
    if (!videoRef.value.ended && !videoRef.value.paused) {
      animationFrameId = requestAnimationFrame(updateFrame)
    } else {
      stopPlay()
    }
  }

  animationFrameId = requestAnimationFrame(updateFrame)
}

// 停止播放
const stopPlay = () => {
  isPlaying.value = false
  if (videoRef.value) {
    void videoRef.value.pause()
  }
  if (animationFrameId !== null) {
    cancelAnimationFrame(animationFrameId)
    animationFrameId = null
  }
}

// 更新预览
const updatePreview = async () => {
  if (!videoRef.value || !canvasRef.value || !videoLoaded.value) {
    return
  }

  await nextTick()

  const video = videoRef.value
  const canvas = canvasRef.value

  // 设置canvas尺寸
  const maxWidth = 800
  const maxHeight = 600
  const videoAspect = video.videoWidth / video.videoHeight
  let canvasWidth = video.videoWidth
  let canvasHeight = video.videoHeight

  if (canvasWidth > maxWidth) {
    canvasWidth = maxWidth
    canvasHeight = maxWidth / videoAspect
  }
  if (canvasHeight > maxHeight) {
    canvasHeight = maxHeight
    canvasWidth = maxHeight * videoAspect
  }

  canvas.width = canvasWidth
  canvas.height = canvasHeight

  // 绘制当前帧到canvas
  const ctx = canvas.getContext('2d')
  if (ctx) {
    ctx.drawImage(video, 0, 0, canvasWidth, canvasHeight)
  }
}

// 模拟预览更新（当视频无法定位时）
const updateSimulatedPreview = async (targetTime: number) => {
  if (!videoRef.value || !canvasRef.value || !videoLoaded.value) {
    return
  }

  await nextTick()

  const video = videoRef.value
  const canvas = canvasRef.value
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    return
  }

  // 设置canvas尺寸
  const maxWidth = 800
  const maxHeight = 600
  const videoAspect = video.videoWidth / video.videoHeight
  let canvasWidth = video.videoWidth
  let canvasHeight = video.videoHeight

  if (canvasWidth > maxWidth) {
    canvasWidth = maxWidth
    canvasHeight = maxWidth / videoAspect
  }
  if (canvasHeight > maxHeight) {
    canvasHeight = maxHeight
    canvasWidth = maxHeight * videoAspect
  }

  canvas.width = canvasWidth
  canvas.height = canvasHeight

  // 绘制第一帧作为基础
  ctx.drawImage(video, 0, 0, canvasWidth, canvasHeight)

  // 添加时间指示器覆盖层
  const progress = targetTime / videoDuration.value

  // 绘制轻微的半透明覆盖层
  ctx.fillStyle = 'rgba(0, 0, 0, 0.2)'
  ctx.fillRect(0, 0, canvasWidth, canvasHeight)

  // 绘制进度条
  const barWidth = canvasWidth * 0.8
  const barHeight = 4
  const barX = (canvasWidth - barWidth) / 2
  const barY = canvasHeight - 20

  // 进度条背景
  ctx.fillStyle = 'rgba(255, 255, 255, 0.4)'
  ctx.fillRect(barX, barY, barWidth, barHeight)

  // 进度条前景
  ctx.fillStyle = '#6366f1'
  ctx.fillRect(barX, barY, barWidth * progress, barHeight)
}

// 确认选择
const onConfirm = () => {
  if (!canvasRef.value || !videoLoaded.value) return

  canvasRef.value.toBlob(
    (blob) => {
      if (blob) {
        emit('confirm', blob, currentTime.value)
        emit('update:visible', false)
      }
    },
    'image/jpeg',
    0.9
  )
}

// 取消
const onCancel = () => {
  emit('cancel')
  emit('update:visible', false)
}

// 关闭弹窗
const onHide = () => {
  stopPlay()
  if (videoRef.value) {
    videoRef.value.currentTime = 0
    videoRef.value.src = ''
  }
  // 清理本地视频URL
  if (localVideoUrl.value && localVideoUrl.value.startsWith('blob:')) {
    URL.revokeObjectURL(localVideoUrl.value)
    localVideoUrl.value = ''
  }
  videoLoaded.value = false
  videoDuration.value = 0
  currentTime.value = 0
  isPlaying.value = false
  emit('update:visible', false)
}

// 监听视频URL变化，重新加载
watch(
  () => props.videoUrl,
  async (newUrl, oldUrl) => {
    // 清理旧的本地视频URL
    if (localVideoUrl.value && localVideoUrl.value.startsWith('blob:')) {
      URL.revokeObjectURL(localVideoUrl.value)
      localVideoUrl.value = ''
    }

    // 重置视频状态
    videoLoaded.value = false
    videoDuration.value = 0
    currentTime.value = 0
    isPlaying.value = false
    stopPlay()

    if (props.visible && videoRef.value && newUrl) {
      // 如果有新视频URL，重新下载
      if (newUrl && newUrl !== oldUrl) {
        try {
          const localUrl = await downloadVideoToLocal(newUrl)
          localVideoUrl.value = localUrl
        } catch (error) {
          console.error('视频下载失败，使用原始URL:', error)
          localVideoUrl.value = ''
        }
      }
      // 重新加载视频
      videoRef.value.load()
    }
  }
)

// 监听visible变化
watch(
  () => props.visible,
  async (newVisible, oldVisible) => {
    if (newVisible && !oldVisible) {
      // 弹窗刚打开时，清理旧状态
      if (localVideoUrl.value && localVideoUrl.value.startsWith('blob:')) {
        URL.revokeObjectURL(localVideoUrl.value)
        localVideoUrl.value = ''
      }

      // 重置视频状态
      videoLoaded.value = false
      videoDuration.value = 0
      currentTime.value = 0
      isPlaying.value = false
      stopPlay()

      // 等待DOM更新
      await nextTick()

      if (videoRef.value && props.videoUrl) {
        // 下载新视频
        try {
          const localUrl = await downloadVideoToLocal(props.videoUrl)
          localVideoUrl.value = localUrl
        } catch (error) {
          console.error('视频下载失败，使用原始URL:', error)
          localVideoUrl.value = ''
        }
      }
    } else if (!newVisible && oldVisible) {
      // 弹窗关闭时清理
      stopPlay()
      if (videoRef.value) {
        videoRef.value.currentTime = 0
        videoRef.value.src = ''
      }
      // 清理本地视频URL
      if (localVideoUrl.value && localVideoUrl.value.startsWith('blob:')) {
        URL.revokeObjectURL(localVideoUrl.value)
        localVideoUrl.value = ''
      }
      videoLoaded.value = false
      videoDuration.value = 0
      currentTime.value = 0
      isPlaying.value = false
    }
  },
  { immediate: true }
)

onMounted(async () => {
  // 如果弹窗已经可见且需要下载视频
  if (
    props.visible &&
    props.videoUrl &&
    !localVideoUrl.value &&
    videoRef.value
  ) {
    try {
      const localUrl = await downloadVideoToLocal(props.videoUrl)
      localVideoUrl.value = localUrl
    } catch (error) {
      console.error('组件挂载后下载失败:', error)
      localVideoUrl.value = ''
    }
  }
})

onUnmounted(() => {
  stopPlay()
  if (videoRef.value) {
    videoRef.value.src = ''
  }

  // 清理本地视频URL
  if (localVideoUrl.value && localVideoUrl.value.startsWith('blob:')) {
    URL.revokeObjectURL(localVideoUrl.value)
    localVideoUrl.value = ''
  }
})
</script>

<style scoped>
.video-frame-selector-content {
  background-color: #1a1a1a;
  color: #ffffff;
}

.controls-section {
  padding: 1rem;
  background: #2d2d2d;
  border-radius: 0.5rem;
}

.time-slider {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: #404040;
  outline: none;
  -webkit-appearance: none;
}

.time-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #6366f1;
  cursor: pointer;
  transition: all 0.2s;
}

.time-slider::-webkit-slider-thumb:hover {
  background: #818cf8;
  transform: scale(1.1);
}

.time-slider::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #6366f1;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.time-slider::-moz-range-thumb:hover {
  background: #818cf8;
  transform: scale(1.1);
}

.preview-section {
  margin-top: 1rem;
}

.preview-container {
  position: relative;
  width: 100%;
  min-height: 300px;
  background: #1a1a1a;
  border: 2px solid #404040;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.preview-canvas {
  max-width: 100%;
  max-height: 400px;
  object-fit: contain;
}

.preview-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

/* 黑色模式下的弹窗样式 */
:deep(.p-dialog) {
  background-color: #1a1a1a;
  color: #ffffff;
}

:deep(.p-dialog-header) {
  background-color: #2d2d2d;
  color: #ffffff;
  border-bottom: 1px solid #404040;
}

:deep(.p-dialog-content) {
  background-color: #1a1a1a;
  color: #ffffff;
}

:deep(.p-button) {
  background-color: #404040;
  border-color: #404040;
  color: #ffffff;
}

:deep(.p-button:hover:not(:disabled)) {
  background-color: #4d4d4d;
  border-color: #4d4d4d;
}

:deep(.p-button.p-button-secondary) {
  background-color: #2d2d2d;
  border-color: #404040;
  color: #ffffff;
}

:deep(.p-button.p-button-secondary:hover) {
  background-color: #3d3d3d;
  border-color: #4d4d4d;
}

:deep(.p-button:disabled) {
  background-color: #1a1a1a;
  border-color: #2d2d2d;
  color: #666666;
  opacity: 0.5;
}
</style>
